// Validation utilities for import data

export interface ValidationError {
  rowIndex: number;
  field: string;
  message: string;
}

/**
 * Validate email format
 */
export const validateEmail = (email: string): boolean => {
  if (!email || email.trim() === "") return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Validate phone format (international format)
 * Supports: +1234567890, (123) 456-7890, 123-456-7890, etc.
 */
export const validatePhone = (phone: string): boolean => {
  if (!phone || phone.trim() === "") return false;
  // Remove common phone formatting characters
  const cleaned = phone.replace(/[\s\-\(\)\+]/g, "");
  // Check if remaining is numeric and has reasonable length (7-15 digits)
  return /^\d{7,15}$/.test(cleaned);
};

/**
 * Validate domain format
 */
export const validateDomain = (domain: string): boolean => {
  if (!domain || domain.trim() === "") return false;
  const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;
  return domainRegex.test(domain.trim());
};

/**
 * Validate date format
 * Supports: ISO format, US format (MM/DD/YYYY), EU format (DD/MM/YYYY)
 */
export const validateDate = (dateStr: string): boolean => {
  if (!dateStr || dateStr.trim() === "") return false;

  // Try ISO format first
  const isoDate = /^\d{4}-\d{2}-\d{2}/.test(dateStr);
  if (isoDate) {
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  }

  // Try other common formats
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

/**
 * Validate a single row
 */
export const validateRow = (
  row: Record<string, string>,
  rowIndex: number,
  mappings: Record<string, any[]>,
  entityType: "CONTACT" | "COMPANY" | "SALE"
): { valid: boolean; errors: ValidationError[] } => {
  const errors: ValidationError[] = [];
  const entityMappings = mappings[entityType] || [];

  entityMappings.forEach((mapping: any) => {
    const sourceField = mapping?.source?.value;
    const destinationField = mapping?.destination?.value;

    if (!sourceField || !destinationField) return;

    const value = row[sourceField] || "";

    // Deal name required (SALE)
    if (entityType === "SALE" && destinationField === "name" && !value.trim()) {
      errors.push({
        rowIndex,
        field: sourceField,
        message: "Deal name is required.",
      });
    }

    // Email validation (CONTACT)
    if (entityType === "CONTACT" && destinationField === "email" && value) {
      if (!validateEmail(value)) {
        errors.push({
          rowIndex,
          field: sourceField,
          message: `Invalid email format: ${value}`,
        });
      }
    }

    // Phone validation (CONTACT)
    if (
      entityType === "CONTACT" &&
      (destinationField === "phone" || destinationField === "mobile") &&
      value
    ) {
      if (!validatePhone(value)) {
        errors.push({
          rowIndex,
          field: sourceField,
          message: `Invalid phone format: ${value}`,
        });
      }
    }

    // Domain validation (CONTACT, COMPANY)
    if (
      (entityType === "CONTACT" || entityType === "COMPANY") &&
      destinationField === "domain" &&
      value
    ) {
      if (!validateDomain(value)) {
        errors.push({
          rowIndex,
          field: sourceField,
          message: `Invalid domain format: ${value}`,
        });
      }
    }

    // Date validation (all entity types: birthday for CONTACT, start/finish for SALE, etc.)
    if (
      (destinationField === "birthday" ||
        destinationField === "start" ||
        destinationField === "finish") &&
      value
    ) {
      if (!validateDate(value)) {
        errors.push({
          rowIndex,
          field: sourceField,
          message: `Invalid date format: ${value}`,
        });
      }
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Find duplicate rows within CSV
 */
export const findDuplicateRows = (
  rows: Record<string, string>[],
  keyFields: string[]
): Map<number, number[]> => {
  const duplicateMap = new Map<number, number[]>();
  const seen = new Map<string, number[]>();

  rows.forEach((row, index) => {
    // Create key from specified fields
    const key = keyFields.map((field) => (row[field] || "").toLowerCase().trim()).join("|");

    if (key) {
      if (!seen.has(key)) {
        seen.set(key, [index]);
      } else {
        const indices = seen.get(key)!;
        indices.push(index);
        seen.set(key, indices);

        // Mark all indices as duplicates
        indices.forEach((idx) => {
          duplicateMap.set(
            idx,
            indices.filter((i) => i !== idx)
          );
        });
      }
    }
  });

  return duplicateMap;
};
